import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, OnInit, input, output, model, effect, computed, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogComponent } from '../../@core/components/ui/dialog/dialog.component';
import { HlmDialogHeaderComponent } from '../../@core/components/spartan/dialog/hlm-dialog-header.component';
import { TextCasePipe } from '../../@shared/pipes/textCase.pipe';
import { ButtonComponent } from '../../@core/components/ui/button/button.component';
import { SelectComponent } from '../../@core/components/ui/select/select.component';
import { SelectOption } from '../../@core/components/ui/select/select.model';
import { SegmentedOption } from '../../@core/components/ui/segmented/segmented.component';
import { SelectButtonComponent } from '../../@core/components/ui/select-button/select-button.component';
import { IconComponent } from '../../@core/components/ui/icon/icon.component';
import { InputComponent } from '../../@core/components/ui/input/input.component';
import { ToggleComponent } from '../../@core/components/ui/toggle/toggle.component';
import { TermUnit, EntityUnit, TERM_UNIT_LABELS, ServiceRateType, IPlan } from '../models';
import { convertToDays } from '../../@core/utils/trial-calculation.utils';
import { MultiSelectComponent } from '../../@core/components/ui/multi-select/multi-select.component';
import { UserNameDisplayComponent } from '../../@shared/components/user-name-display/user-name-display.component';
import { IUser } from '../../@core/models/model';
import { UserService } from '../../contract-management/services/user.service';
import { SubscriptionStateService } from '../../@core/services/subscription-state.service';
import { OrganizationSetupFormService } from '../../contract-management/services/organization-setup-from.service';
import { PaymentService } from '../../contract-management/services/payment.service';
import { getPlanChangeCost, isDowngradePlan } from '../plan-pricing.utils';
import { isPlanFeatureAvailable, UI_FEATURE, UI_FEATURE_LABELS } from '../../@core/models/feature-mapping';

export enum CheckoutSource {
    INVITE = 'INVITE',
    ORG_PLAN_CHANGE = 'ORG_PLAN_CHANGE',
    NONE = ''
}

export interface CheckoutAddon {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
    price: number;
}

export interface CheckoutConfig {
    planId: string;
    numberOfUsers: number;
    userIds?: string[];
    setupFee: boolean;
    trainingFee: boolean;
    aiEducationFee: boolean;
    customFees: string[]; // names of opted-in optional custom fees
    proratedCharge?: number;
    billing: {
        askBudget: number;
        agentBudget: number;
        price: number;
        entity: EntityUnit;
        term: TermUnit;
    }
}

// Interface for individual addon
export interface PlanAddon {
    id: string;
    name: string;
    description: string;
    enabled: boolean;
    price: number;
    type: ServiceRateType;
}

@Component({
    selector: 'app-checkout-dialog',
    standalone: true,
    imports: [CommonModule, DecimalPipe, FormsModule, DialogComponent, HlmDialogHeaderComponent, TextCasePipe, ButtonComponent, SelectComponent, IconComponent, InputComponent, SelectButtonComponent, ToggleComponent, MultiSelectComponent, UserNameDisplayComponent],
    templateUrl: './checkout-dialog.component.html',
    styleUrl: './checkout-dialog.component.scss'
})
export class CheckoutDialogComponent implements OnInit {

    EntityUnit = EntityUnit;
    TermUnit = TermUnit;
    ServiceRateType = ServiceRateType;
    CheckoutSource = CheckoutSource;

    visible = model<boolean>(false);
    plan = input<any>();
    plans = input<any[]>([]);
    selectedEntityType = input<EntityUnit>(EntityUnit.USER);
    users = input<IUser[]>([]);
    submitLabel = input<string>('Proceed to payment');
    showAddons = input<boolean>(true);
    showUsers = input<boolean>(true);
    filterByOrgBillingCycle = input<boolean>(false);
    showPlan = input<boolean>(true);
    showTrial = input<boolean>(true);
    inviteEmails = input<string[]>([]);
    inviteMembers = input<Array<{ email: string, role?: string, planId?: string, planName?: string }>>([]);
    isDowngrade = input<boolean>(false);
    source = input<CheckoutSource>(CheckoutSource.NONE);
    sourcePlan = input<IPlan | null>(null);
    onProceed = output<CheckoutConfig>();

    orgBillingCycle = computed<TermUnit | null>(() => {
        const subs = this.subscriptionState.orgSubscriptionDetails();
        return ((subs as any)?.billing?.term as TermUnit) ?? TermUnit.MONTH;
    });

    protected readonly hasActiveOrgPlan = computed(() => this.subscriptionState.orgIsActive());

    protected readonly isDowngradeScheduled = computed(() =>
        this.isDowngrade() &&
        this.hasActiveOrgPlan() &&
        this.subscriptionState.orgHasPaid() &&
        !this.subscriptionState.orgIsTrial()
    );

    allOrgUsers = signal<IUser[]>([]);
    activeUsersCount = signal<number>(0);
    usersPlanMap = signal<Record<string, any>>({});
    selectedComparisonUserId = signal<string>('');

    selectedCheckoutUserIds: string[] = [];
    checkoutUserOptions = computed(() => {
        const allUsers = this.allOrgUsers();
        const baseUsers = allUsers.length > 0 ? allUsers : this.users();
        return baseUsers.map(u => ({ label: u.fullName || u.email, value: u._id, data: u }));
    });

    inviteEmailOptions = computed(() =>
        this.inviteEmails().map(email => ({ label: email, value: email, data: null }))
    );

    selectedPlan: any;

    // Computed property to convert plans to SelectOption format
    planOptions = computed<SelectOption[]>(() => {
        let plans = this.plans();
        if (this.filterByOrgBillingCycle()) {
            const cycle = this.orgBillingCycle();
            if (cycle) {
                plans = plans
                    .filter((plan: any) => plan.pricing?.some((p: any) => p.term === cycle))
                    .map((plan: any) => ({
                        ...plan,
                        pricing: plan.pricing.filter((p: any) => p.term === cycle)
                    }));
            }
        }
        return plans.map((plan: any) => ({
            label: plan.name,
            value: plan,
            disabled: false
        }));
    });

    // Computed property to generate billing cycle options based on plan pricing
    billingCycleOptions = computed<SegmentedOption[]>(() => {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) {
            return [];
        }

        return currentPlan.pricing.map((pricing: any) => {
            const termLabel = this.getTermLabel(pricing.term);
            const entityLabel = pricing.entity === EntityUnit.USER ? 'user' : 'org';
            const label = `$${pricing.price}/${entityLabel}/${termLabel.toLowerCase()}`;

            return {
                label: label,
                value: pricing.term.toLowerCase()
            };
        });
    });

    // Getter for term options as segmented button options (filtered by selected entity)
    // Using getter instead of computed to react to selectedPlan changes
    get termSegmentedOptions(): SegmentedOption[] {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) {
            return [];
        }

        const options = currentPlan.pricing
            .filter((pricing: any) => pricing.entity === this.selectedEntity())
            .map((pricing: any) => {
                const termLabel = this.getTermLabel(pricing.term);

                return {
                    label: termLabel,
                    value: pricing.term
                };
            });
        return options;
    }

    // Getter for term options with price included in label
    // Using getter instead of computed to react to selectedPlan changes
    get termSegmentedOptionsWithPrice(): SegmentedOption[] {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) {
            return [];
        }

        const options = currentPlan.pricing
            .filter((pricing: any) => pricing.entity === this.selectedEntity())
            .map((pricing: any) => {
                const termLabel = this.getTermLabel(pricing.term);
                const price = pricing.price;

                return {
                    label: `${termLabel} $${price}`,
                    value: pricing.term
                };
            });
        return options;
    }

    get currentPlan(): any {
        return this.selectedPlan || this.plan();
    }

    // Check if plan has both USER and ORGANIZATION entities
    // Using getter instead of computed to react to selectedPlan changes
    get hasBothEntities(): boolean {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) {
            return false;
        }

        const hasUser = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.USER);
        const hasOrg = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.ORGANIZATION);

        return hasUser && hasOrg;
    }

    // Get available entities for the current plan
    get availableEntities(): EntityUnit[] {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) {
            return [];
        }

        const entities: EntityUnit[] = [];
        const hasUser = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.USER);
        const hasOrg = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.ORGANIZATION);

        if (hasUser) {
            entities.push(EntityUnit.USER);
        }
        if (hasOrg) {
            entities.push(EntityUnit.ORGANIZATION);
        }

        return entities;
    }

    getTermLabel(term: TermUnit | string): string {
        const termKey = typeof term === 'string' ? term.toUpperCase() as TermUnit : term;
        return TERM_UNIT_LABELS[termKey] || term.toString();
    }

    // Helper methods for UI variations
    getPriceForCycle(cycle: string): number {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.pricing) return 0;

        // Use selectedEntity() directly - no string manipulation needed
        const targetEntity = this.selectedEntity();

        const pricing = currentPlan.pricing.find((p: any) => {
            const termMatches = p.term.toLowerCase() === cycle.toLowerCase();
            const entityMatches = p.entity === targetEntity;
            return termMatches && entityMatches;
        });

        return pricing ? pricing.price : 0;
    }

    getEntityLabel(cycle?: string): string {
        return this.selectedEntity();
    }

    onEntityToggleChange(isOrganization: boolean): void {
        this.selectedEntity.set(isOrganization ? EntityUnit.ORGANIZATION : EntityUnit.USER);

        // Reset billing cycle to first available option for the new entity
        const options = this.termSegmentedOptions;

        if (options.length > 0) {
            this.billingCycle = options[0].value as TermUnit;
            this.updatePricePerUser();
        }
    }

    onEntityRadioChange(entity: EntityUnit): void {
        this.selectedEntity.set(entity);

        // Reset billing cycle to first available option for the new entity
        const options = this.termSegmentedOptions;

        if (options.length > 0) {
            this.billingCycle = options[0].value as TermUnit;
            this.updatePricePerUser();
        }
    }

    onEntityChange(value: EntityUnit) {
        this.selectedEntity.set(value);
        this.isOrganizationEntity = value === EntityUnit.ORGANIZATION;

        // Reset billing cycle to first available option for the new entity
        const options = this.termSegmentedOptions;

        if (options.length > 0) {
            this.billingCycle = options[0].value as TermUnit;
        }

        // Update price per user after billing cycle is set
        this.updatePricePerUser();
    }

    getCurrentPrice(): number {
        return this.getPriceForCycle(this.billingCycle);
    }

    getTotalForCycle(): number {
        const price = this.getCurrentPrice();
        // If entity is ORGANIZATION, return flat price (not multiplied by users)
        if (this.selectedEntity() === EntityUnit.ORGANIZATION) {
            return price;
        }
        return price * this.numberOfUsers;
    }

    getMonthlyEquivalent(cycle: string): number {
        const price = this.getPriceForCycle(cycle);
        if (cycle.toLowerCase() === TermUnit.YEAR) {
            return Math.round(price / 12);
        }
        return price;
    }

    getYearlySavings(): number {
        const monthlyPrice = this.getPriceForCycle('month');
        const yearlyPrice = this.getPriceForCycle('year');

        if (!monthlyPrice || !yearlyPrice) return 0;

        const yearlyAsMonthly = monthlyPrice * 12;
        return Math.max(0, yearlyAsMonthly - yearlyPrice);
    }

    onComparisonUserChange(userId: string): void {
        this.selectedComparisonUserId.set(userId);

        if (!this.selectedPlan) return;

        const options = this.filteredPlanOptions;
        const isStillAvailable = options.some(opt => opt.value._id === this.selectedPlan._id);

        if (!isStillAvailable && options.length > 0) {
            this.selectedPlan = options[0].value;
            this.onPlanChange();
        }
    }

    get filteredCheckoutUserOptions(): SelectOption[] {
        return this.checkoutUserOptions().filter(opt => this.selectedCheckoutUserIds.includes(opt.value));
    }

    get filteredPlanOptions(): SelectOption[] {
        const currentPlanId = this.comparisonPlan?.linkedDefaultPlan ?? this.comparisonPlan?._id;
        if (!currentPlanId) return this.planOptions();
        return this.planOptions().filter(opt => opt.value._id !== currentPlanId);
    }

    get comparisonPlan(): IPlan | null {
        if (this.filterByOrgBillingCycle() && this.selectedComparisonUserId()) {
            const planMap = this.usersPlanMap();
            const userPlanData = planMap[this.selectedComparisonUserId()];
            if (userPlanData) {
                const plan = (userPlanData as any)?.plan ?? userPlanData;
                if (plan && typeof plan === 'object' && plan.pricing) {
                    return plan as IPlan;
                }
            }
        }
        return this.sourcePlan() ?? this.subscriptionState.orgPlan();
    }

    get featureRows(): { label: string; inCurrent: boolean; inTarget: boolean }[] {
        const target = this.selectedPlan || this.plan();
        const source = this.comparisonPlan;
        if (!source || !target) return [];
        return Object.values(UI_FEATURE)
            .filter(f => isPlanFeatureAvailable(source, f) || isPlanFeatureAvailable(target as IPlan, f))
            .map(f => ({
                label: UI_FEATURE_LABELS[f],
                inCurrent: isPlanFeatureAvailable(source, f),
                inTarget: isPlanFeatureAvailable(target as IPlan, f)
            }));
    }

    get lostFeaturesCount(): number {
        return this.featureRows.filter(r => r.inCurrent && !r.inTarget).length;
    }

    get gainedFeaturesCount(): number {
        return this.featureRows.filter(r => !r.inCurrent && r.inTarget).length;
    }

    get hasFeaturesLost(): boolean {
        return this.lostFeaturesCount > 0 && this.isDowngrade();
    }

    get hasFeaturesGained(): boolean {
        return this.gainedFeaturesCount > 0 && !this.isDowngrade();
    }

    getCurrentDescription(): string {
        const price = this.getCurrentPrice();
        const entity = this.getEntityLabel();
        const cycle = this.billingCycle;

        return `$${price} per ${entity}, billed ${cycle}ly`;
    }

    selectBillingCycle(cycleValue: string): void {
        this.billingCycle = cycleValue as TermUnit;
        this.updatePricePerUser();
    }

    updatePricePerUser(): void {
        const orgSubs = this.subscriptionState.orgSubscriptionDetails();
        const cycleString = this.source() === CheckoutSource.ORG_PLAN_CHANGE ? this.billingCycle : (orgSubs?.billing?.term as TermUnit) ?? this.billingCycle;

        const targetPlan = this.selectedPlan || this.plan();
        const orgPlan = this.subscriptionState.orgPlan();

        if (
            targetPlan && orgPlan && orgSubs?.billing?.price != null &&
            String(targetPlan._id) === String(orgPlan._id) &&
            this.source() != CheckoutSource.ORG_PLAN_CHANGE
        ) {
            this.pricePerUser = orgSubs.billing.price;
            return;
        }

        const rawPrice = this.getPriceForCycle(cycleString);

        if (this.filterByOrgBillingCycle()) {
            const orgCycle = this.orgBillingCycle();
            const targetPlan = this.selectedPlan || this.plan();
            if (orgCycle && targetPlan) {
                const planMap = this.usersPlanMap();
                const selectedIds = this.selectedCheckoutUserIds;
                const orgPlan = this.subscriptionState.orgPlan();

                if (selectedIds.length > 0) {
                    const isInviteFlow = this.source() === CheckoutSource.INVITE;
                    const totalCost = selectedIds.reduce((sum, userId) => {
                        const userCurrentPlan = isInviteFlow ? null : (planMap[userId]?.plan as IPlan | undefined) ?? orgPlan;
                        return sum + getPlanChangeCost(userCurrentPlan, targetPlan, orgCycle);
                    }, 0);
                    this.pricePerUser = Math.round((totalCost / selectedIds.length) * 100) / 100;
                    return;
                }

                this.pricePerUser = getPlanChangeCost(orgPlan, targetPlan, orgCycle);
                return;
            }
        }

        this.pricePerUser = rawPrice;
    }

    constructor(private userService: UserService, private subscriptionState: SubscriptionStateService, private orgSetupService: OrganizationSetupFormService, private paymentService: PaymentService) {
        effect(() => {
            const options = this.planOptions();
            const defaultPlan = this.plan();
            if (!defaultPlan || options.length === 0) return;
            const match = options.find((opt) => opt.value._id === (defaultPlan)._id);
            if (match) {
                this.selectedPlan = match.value;
                this.onPlanChange();
            }
        });
    }

    billingCycle: TermUnit = TermUnit.MONTH;
    selectedEntity = signal<EntityUnit>(EntityUnit.USER); // Signal for reactive updates
    isOrganizationEntity: boolean = false; // For toggle: false = user, true = organization

    // Simplified getter/setter for ngModel binding with radio buttons
    get selectedEntityValue(): EntityUnit {
        return this.selectedEntity();
    }

    set selectedEntityValue(value: EntityUnit) {
        this.onEntityChange(value);
    }

    isSubmitting = signal(false);
    proratedCharge = signal<number | null>(null);
    isLoadingProratedCharge = signal(false);

    numberOfUsers = 5;
    pricePerUser = 49;

    subscriptionStartDate = '';
    renewalDate = '';

    get trialDays(): number {
        if (this.source() === CheckoutSource.ORG_PLAN_CHANGE) {
            return 0;
        }
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan || !currentPlan.trial || !currentPlan.trial.enabled) {
            return 0;
        }
        return convertToDays(currentPlan.trial.duration, currentPlan.trial.term);
    }

    get shouldShowTrial(): boolean {
        return this.trialDays > 0 && this.showTrial();
    }

    // Getter to get add-ons from plan as key-value pairs
    // Using getter instead of computed to react to selectedPlan changes
    get planAddons(): {
        setupFee?: PlanAddon;
        trainingFee?: PlanAddon;
        aiEducationFee?: PlanAddon;
        customFees: PlanAddon[];
    } {
        const currentPlan = this.selectedPlan || this.plan();
        if (!currentPlan) {
            return { customFees: [] };
        }

        const orgPlan = this.subscriptionState.orgPlan();

        const addons: {
            setupFee?: PlanAddon;
            trainingFee?: PlanAddon;
            aiEducationFee?: PlanAddon;
            customFees: PlanAddon[];
        } = { customFees: [] };

        // Setup Fee
        if (currentPlan.setupFee) {
            addons.setupFee = {
                id: 'setup',
                name: orgPlan?.setupFee?.name || currentPlan.setupFee.name || 'Setup',
                description: 'One-time enablement for faster rollout.',
                enabled: currentPlan.setupFee.type === ServiceRateType.REQUIRED ? true : (currentPlan.setupFee.customerOptedIn || false),
                price: currentPlan.setupFee.cost,
                type: currentPlan.setupFee.type
            };
        }

        // Training Fee
        if (currentPlan.trainingFee) {
            addons.trainingFee = {
                id: 'training',
                name: orgPlan?.trainingFee?.name || currentPlan.trainingFee.name || 'Training',
                description: 'Get everyone up to speed with our platform.',
                enabled: currentPlan.trainingFee.type === ServiceRateType.REQUIRED ? true : (currentPlan.trainingFee.customerOptedIn || false),
                price: currentPlan.trainingFee.cost,
                type: currentPlan.trainingFee.type
            };
        }

        // AI Education Fee
        if (currentPlan.aiEducationFee) {
            addons.aiEducationFee = {
                id: 'ai-education',
                name: orgPlan?.aiEducationFee?.name || currentPlan.aiEducationFee.name || 'AI Education',
                description: 'Learn how AI can help your team be more productive.',
                enabled: currentPlan.aiEducationFee.type === ServiceRateType.REQUIRED ? true : (currentPlan.aiEducationFee.customerOptedIn || false),
                price: currentPlan.aiEducationFee.cost,
                type: currentPlan.aiEducationFee.type
            };
        }

        // Custom Fees
        addons.customFees = (currentPlan.customFees || []).map((fee: any) => ({
            id: fee.name,
            name: fee.name,
            description: fee.description || '',
            enabled: fee.type === ServiceRateType.REQUIRED ? true : (fee.customerOptedIn || false),
            price: fee.cost,
            type: fee.type
        }));

        return addons;
    }

    ngOnInit() {
        const defaultPlan = this.plan();
        const options = this.planOptions();
        if (defaultPlan && options.length > 0) {
            const match = options.find((opt: any) => opt.value._id === (defaultPlan as any)._id);
            this.selectedPlan = match ? match.value : defaultPlan;
        } else {
            this.selectedPlan = defaultPlan;
        }

        // Initialize selectedEntity with the passed selectedEntityType ONLY ONCE
        const entityType = this.selectedEntityType();
        this.selectedEntity.set(entityType);
        this.isOrganizationEntity = entityType === EntityUnit.ORGANIZATION;

        // If no pricing available for the selected entity, fall back to first available entity
        if (this.selectedPlan?.pricing?.length > 0) {
            const hasEntityPricing = this.selectedPlan.pricing.some(
                (p: any) => p.entity === this.selectedEntity()
            );
            if (!hasEntityPricing) {
                const firstEntity = this.selectedPlan.pricing[0].entity as EntityUnit;
                this.selectedEntity.set(firstEntity);
                this.isOrganizationEntity = firstEntity === EntityUnit.ORGANIZATION;
            }
        }

        // Lock billing cycle to org's current term when filtering by org cycle
        if (this.filterByOrgBillingCycle()) {
            const orgCycle = this.orgBillingCycle();
            if (orgCycle) {
                this.billingCycle = orgCycle;
            }
        } else {
            const options = this.termSegmentedOptions;
            if (options.length > 0) {
                this.billingCycle = options[0].value as TermUnit;
            }
        }

        this.selectedCheckoutUserIds = this.inviteEmails().length > 0
            ? [...this.inviteEmails()]
            : this.users().map(u => u._id);
        if (this.filterByOrgBillingCycle() && this.selectedCheckoutUserIds.length > 0) {
            this.selectedComparisonUserId.set(this.selectedCheckoutUserIds[0]);
        }
        this.calculateDates();
        this.updatePricePerUser();

        this.userService.getUsers().subscribe(res => {
            if (res.success) {
                this.allOrgUsers.set(res.data.users);
                this.activeUsersCount.set(res.data.activeCount ?? 0);
                if (this.numberOfUsers < this.minUsers) {
                    this.numberOfUsers = this.minUsers;
                }
            }
        });

        if (this.filterByOrgBillingCycle()) {
            this.orgSetupService.getOrgUsersPlanMap().subscribe((res: any) => {
                if (res.success) {
                    this.usersPlanMap.set(res.data);
                    this.updatePricePerUser();
                }
            });
        }
    }

    calculateDates() {
        const today = new Date();
        const nextBillingDate = this.subscriptionState.orgSubscriptionDetails()?.nextBillingDate;

        if (this.source() === CheckoutSource.ORG_PLAN_CHANGE && !this.isDowngrade()) {
            this.subscriptionStartDate = this.formatDate(today);
            this.renewalDate = nextBillingDate
                ? this.formatDate(new Date(nextBillingDate))
                : this.formatDate(this.addBillingCycle(today));
            return;
        }

        if (this.source() === CheckoutSource.INVITE || nextBillingDate) {
            const date = nextBillingDate ? new Date(nextBillingDate) : today;
            this.subscriptionStartDate = this.formatDate(date);
            this.renewalDate = this.formatDate(date);
            return;
        }

        const trialEndDate = new Date(today.getTime() + this.trialDays * 24 * 60 * 60 * 1000);
        this.subscriptionStartDate = this.formatDate(trialEndDate);
        this.renewalDate = this.formatDate(this.addBillingCycle(trialEndDate));
    }

    private addBillingCycle(date: Date): Date {
        const renewalDate = new Date(date);
        const cycleUpper = this.billingCycle.toUpperCase() as TermUnit;
        if (cycleUpper === TermUnit.MONTH) {
            renewalDate.setMonth(renewalDate.getMonth() + 1);
        } else if (cycleUpper === TermUnit.YEAR) {
            renewalDate.setFullYear(renewalDate.getFullYear() + 1);
        } else if (cycleUpper === TermUnit.DAY) {
            renewalDate.setDate(renewalDate.getDate() + 1);
        }
        return renewalDate;
    }

    formatDate(date: Date): string {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    get minUsers(): number {
        const planMin = this.currentPlan?.userRestrictions?.min || 1;
        return Math.max(planMin, this.activeUsersCount());
    }

    get maxUsers(): number | null {
        const raw = this.currentPlan?.userRestrictions?.max;
        if (!raw || raw === 0) return null;
        return Math.max(raw, this.minUsers);
    }

    onNumberOfUsersBlur() {
        if (!this.numberOfUsers || this.numberOfUsers < this.minUsers) {
            this.numberOfUsers = this.minUsers;
        } else if (this.maxUsers !== null && this.numberOfUsers > this.maxUsers) {
            this.numberOfUsers = this.maxUsers;
        }
    }

    decrementUsers() {
        if (this.numberOfUsers > this.minUsers) {
            this.numberOfUsers--;
        }
    }

    incrementUsers() {
        if (this.maxUsers === null || this.numberOfUsers < this.maxUsers) {
            this.numberOfUsers++;
        }
    }

    toggleAddon(addon: any) {
        // Only allow toggle if type is OPTIONAL
        if (addon.type === ServiceRateType.REQUIRED) {
            return;
        }

        // Update the plan's fee property
        const currentPlan = this.selectedPlan || this.plan();
        if (currentPlan && currentPlan[addon.feeProperty]) {
            currentPlan[addon.feeProperty].customerOptedIn = !currentPlan[addon.feeProperty].customerOptedIn;
        }
    }

    onAddonToggleChange(feeProperty: string, isEnabled: boolean) {
        // Update the plan's fee property
        const currentPlan = this.selectedPlan || this.plan();
        if (currentPlan && currentPlan[feeProperty]) {
            // Only allow toggle if type is OPTIONAL
            if (currentPlan[feeProperty].type === ServiceRateType.REQUIRED) {
                return;
            }
            currentPlan[feeProperty].customerOptedIn = isEnabled;
        }
    }

    isAddonDisabled(addon: any): boolean {
        return addon.type === ServiceRateType.REQUIRED;
    }

    getPlanTotal(): number {
        if (this.selectedEntity() === EntityUnit.ORGANIZATION) {
            return this.pricePerUser;
        }

        const userCount = (this.filterByOrgBillingCycle() && this.selectedCheckoutUserIds.length > 0)
            ? this.selectedCheckoutUserIds.length
            : this.numberOfUsers;

        return userCount * this.pricePerUser;
    }

    getAddonsTotal(): number {
        if (!this.showAddons()) return 0;
        const addons = this.planAddons;
        const fixed = [addons.setupFee, addons.trainingFee, addons.aiEducationFee]
            .filter(addon => addon?.enabled)
            .reduce((sum, addon) => sum + (addon?.price || 0), 0);
        const custom = (addons.customFees || [])
            .filter(addon => addon?.enabled)
            .reduce((sum, addon) => sum + (addon?.price || 0), 0);
        return fixed + custom;
    }

    getTotal(): number {
        return this.getPlanTotal() + this.getAddonsTotal();
    }

    get inviteLineItems(): Array<{ email: string, planName: string, price: number }> {
        const members = this.inviteMembers();
        if (!members.length) return [];
        const cycle = (this.orgBillingCycle() || TermUnit.MONTH).toLowerCase();
        const allPlans = this.plans();
        const defaultPlan = this.plan();
        return members.map(member => {
            const plan = member.planId
                ? allPlans.find((p: any) => p._id === member.planId) ?? defaultPlan
                : defaultPlan;
            const pricing = plan?.pricing?.find((p: any) =>
                p.term?.toLowerCase() === cycle && p.entity === EntityUnit.USER
            );
            return {
                email: member.email,
                planName: plan?.name || member.planName || 'Default Plan',
                price: pricing?.price ?? 0
            };
        });
    }

    getInviteTotal(): number {
        return this.inviteLineItems.reduce((sum, item) => sum + item.price, 0);
    }

    getFrequencyLabel(): string {
        return this.getTermLabel(this.billingCycle);
    }

    hasEnabledAddons(): boolean {
        if (!this.showAddons()) return false;
        const addons = this.planAddons;
        return [addons.setupFee, addons.trainingFee, addons.aiEducationFee].some(addon => addon?.enabled)
            || (addons.customFees || []).some(addon => addon?.enabled);
    }

    onCustomFeeToggle(index: number, isEnabled: boolean) {
        const currentPlan = this.selectedPlan || this.plan();
        if (currentPlan?.customFees?.[index]) {
            if (currentPlan.customFees[index].type === ServiceRateType.REQUIRED) return;
            currentPlan.customFees[index].customerOptedIn = isEnabled;
        }
    }

    fetchOrgProratedCharge(): void {
        if (this.source() !== CheckoutSource.ORG_PLAN_CHANGE) return;
        const plan = this.selectedPlan || this.plan();
        if (!plan?._id) return;
        this.isLoadingProratedCharge.set(true);
        this.paymentService.getOrgProratedCharge(plan._id, this.selectedEntity(), this.billingCycle).subscribe({
            next: (res) => {
                this.proratedCharge.set(res.success && res.data ? (res.data.proratedCharge ?? 0) : null);
                this.isLoadingProratedCharge.set(false);
            },
            error: () => {
                this.proratedCharge.set(null);
                this.isLoadingProratedCharge.set(false);
            }
        });
    }

    onBillingCycleChange(value: any) {
        this.billingCycle = value;
        this.updatePricePerUser();
        this.calculateDates();
        this.fetchOrgProratedCharge();
    }

    keepCurrentPlan(): void {
        this.cancel();
    }

    cancel() {
        this.isSubmitting.set(false);
        this.visible.set(false);
    }

    proceedToPayment() {
        this.isSubmitting.set(true);
        if (!this.showPlan() && !this.selectedPlan) {
            this.onProceed.emit({ planId: '', numberOfUsers: 0, setupFee: false, trainingFee: false, aiEducationFee: false, customFees: [], billing: { askBudget: 0, agentBudget: 0, price: 0, entity: this.selectedEntity(), term: this.billingCycle } });
            return;
        }
        const addons = this.planAddons;
        const selectedCustomFees = (addons.customFees || [])
            .filter(addon => addon?.enabled)
            .map(addon => addon.id);

        // Only send the prorated charge for a mid-cycle org upgrade (not downgrades, where it
        // takes effect next cycle, and not fresh signups, where there is no current cycle).
        const proratedCharge = (this.source() === CheckoutSource.ORG_PLAN_CHANGE && !this.isDowngrade())
            ? (this.proratedCharge() ?? undefined)
            : undefined;

        const config: CheckoutConfig = {
            planId: this.selectedPlan._id,
            // For ORGANIZATION entity, numberOfUsers should be 1 or the actual count for record keeping
            numberOfUsers: this.selectedEntity() === EntityUnit.ORGANIZATION ? 1 : this.numberOfUsers,
            userIds: this.selectedCheckoutUserIds.length ? this.selectedCheckoutUserIds : undefined,
            setupFee: addons.setupFee?.enabled || false,
            trainingFee: addons.trainingFee?.enabled || false,
            aiEducationFee: addons.aiEducationFee?.enabled || false,
            customFees: selectedCustomFees,
            proratedCharge: ((proratedCharge !== undefined) ? (proratedCharge * this.numberOfUsers) : undefined),
            billing: {
                askBudget: this.selectedPlan.pricing.find((p: any) => (p.entity === EntityUnit.USER && p.term === this.billingCycle))?.askBudget,
                agentBudget: this.selectedPlan.pricing.find((p: any) => (p.entity === EntityUnit.USER && p.term === this.billingCycle))?.agentBudget,
                price: this.pricePerUser,
                entity: this.selectedEntity(),
                term: this.billingCycle

            }
        };
        this.onProceed.emit(config);
    }

    onPlanChange() {
        // Update selectedPlan with the new plan from the select component
        // The select component binds to selectedPlan, so it's already updated
        // But we need to reset everything based on the new plan

        // Ensure selectedPlan is set (in case it wasn't updated by ngModel)
        if (!this.selectedPlan) {
            this.selectedPlan = this.plan();
        }

        const currentPlan = this.selectedPlan;
        if (!currentPlan || !currentPlan.pricing || currentPlan.pricing.length === 0) {
            return;
        }

        // Check what entities are available in the new plan
        const hasUser = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.USER);
        const hasOrg = currentPlan.pricing.some((p: any) => p.entity === EntityUnit.ORGANIZATION);
        const hasBoth = hasUser && hasOrg;

        // Determine which entity to select based on what's available
        let entityToSelect: EntityUnit;

        if (hasBoth) {
            // Plan has both entities - check if current entity exists, otherwise prefer USER
            const currentEntity = this.selectedEntity();
            const hasCurrentEntity = currentPlan.pricing.some((p: any) => p.entity === currentEntity);

            if (hasCurrentEntity) {
                // Keep current entity if it exists in the new plan
                entityToSelect = currentEntity;
            } else {
                // Default to USER if current entity doesn't exist
                entityToSelect = EntityUnit.USER;
            }
        } else if (hasUser) {
            // Plan has only USER - automatically select USER
            entityToSelect = EntityUnit.USER;
        } else if (hasOrg) {
            // Plan has only ORGANIZATION - automatically select ORGANIZATION
            entityToSelect = EntityUnit.ORGANIZATION;
        } else {
            // Fallback: use first available entity
            const firstPricing = currentPlan.pricing[0];
            entityToSelect = firstPricing ? firstPricing.entity : EntityUnit.USER;
        }

        // Update selected entity
        this.selectedEntity.set(entityToSelect);
        this.isOrganizationEntity = entityToSelect === EntityUnit.ORGANIZATION;

        // Set billing cycle: locked to orgBillingCycle when filtering, otherwise first available
        if (this.filterByOrgBillingCycle()) {
            const orgCycle = this.orgBillingCycle();
            if (orgCycle) {
                this.billingCycle = orgCycle;
            }
        } else {
            const availablePricing = currentPlan.pricing.filter((p: any) => p.entity === entityToSelect);
            if (availablePricing.length > 0) {
                this.billingCycle = availablePricing[0].term as TermUnit;
            } else if (currentPlan.pricing.length > 0) {
                this.billingCycle = currentPlan.pricing[0].term as TermUnit;
            }
        }

        // Reset numberOfUsers based on plan restrictions if needed
        if (this.numberOfUsers < this.minUsers) {
            this.numberOfUsers = this.minUsers;
        }
        if (this.maxUsers !== null && this.numberOfUsers > this.maxUsers) {
            this.numberOfUsers = this.maxUsers;
        }

        // Update price per user based on new plan
        this.updatePricePerUser();

        // Recalculate dates based on new billing cycle
        this.calculateDates();

        // Refresh prorated charge for org plan change
        this.fetchOrgProratedCharge();
    }
}
